import { FourArrows, QuotesIcon } from "assets/website";
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  company?: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Through Precision's process, we hired the best-fit Program Management candidate who is already exceeding expectations. If you want to hire with confidence and peace of mind, I highly recommend Precision.",
    name: "Board Lead",
    role: "Domain Influence Leadership Initiative",
  },
  {
    quote: "I don't think I'll ever hire without Precision again. The difference between candidates who went through Precision and those who didn't is very clear.",
    name: "Josemaria Agulanna",
    role: "CEO",
    company: "Keble",
  },
  {
    quote: "Precision cuts through the clutter of LinkedIn applications and helps us find talent that truly fits our team without exceeding our budget.",
    name: "Osaru Alile",
    role: "Creative Director",
    company: "CC Interiors",
  },
  {
    quote: "Precision delivers talent intelligence in a clear, practical, and highly effective way. Using this screening tool has saved us from engaging unsuitable candidates. The insights give us a complete view of talent effectiveness.",
    name: "David Idibe",
    role: "Founder",
    company: "Dezyncle",
  },
  {
    quote: "Precision consistently helps us select the most effective talent. Their reports are insightful, honest, and give us a clear picture of what to expect before hiring.",
    name: "Sayo Odunsi",
    role: "Principal",
    company: "360° Marketing",
  },
];

const CustomersSaySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext]);

  const getCardPosition = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return "center";
    if (diff === 1) return "right-1";
    if (diff === 2) return "right-2";
    if (diff === -1) return "left-1";
    if (diff === -2) return "left-2";
    
    // Handle wraparound for circular navigation
    const total = testimonials.length;
    if (diff === -(total - 1)) return "right-1";
    if (diff === -(total - 2)) return "right-2";
    if (diff === total - 1) return "left-1";
    if (diff === total - 2) return "left-2";
    
    return "hidden";
  };
  return (
    <section className="py-16 md:py-24 bg-white/70 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div
            data-aos="fade-up"
            className="inline-flex py-2 items-center gap-2 mb-4 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)]"
          >
            <FourArrows />
            <span className="sm:text-lg text-base text-[#050B17] tracking-wider">
              Our Client
            </span>
          </div>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17]"
          >
            Customers Say
          </h2>
        </div>

        {/* Testimonials Grid - Masonry-like layout */}
        <div className="relative mx-4 sm:mx-8 md:mx-12 lg:mx-16 xl:mx-20 h-[350px] sm:h-[400px] md:h-[500px] lg:h-[550px] xl:h-[600px] flex items-center justify-center py-8 sm:py-10 md:py-16 lg:py-20">
          <div className="relative w-full h-full flex items-center justify-center">
            {testimonials.map((testimonial, index) => {
              const position = getCardPosition(index);
              const isCenter = position === "center";
              return (
                <motion.div
                  key={index}
                  className="absolute cursor-pointer"
                  initial={false}
                  animate={{
                    x: position === "center" ? "0%" :
                      position === "left-1" ? "-55%" :
                      position === "left-2" ? "-105%" :
                      position === "right-1" ? "55%" :
                      position === "right-2" ? "105%" : "0%",
                    scale: position === "center" ? 1 :
                      position === "left-1" || position === "right-1" ? 0.85 :
                      position === "left-2" || position === "right-2" ? 0.7 : 0.5,
                    zIndex: position === "center" ? 10 :
                      position === "left-1" || position === "right-1" ? 8 :
                      position === "left-2" || position === "right-2" ? 6 :
                      position === "hidden" ? 0 : 5,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut",
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                  }}
                  style={{
                    pointerEvents: "auto",
                  }}
                  onClick={() => setCurrentIndex(index)}
                >
                  <div className={`w-[260px] sm:w-[280px] md:w-[320px] lg:w-[360px] xl:w-[398px] h-[320px] sm:h-[360px] md:h-[440px] lg:h-[500px] xl:h-[566px] bg-white rounded-[16px] sm:rounded-[18px] md:rounded-[20px] shadow-[0px_0px_7px_0px_#00000026] flex flex-col pt-6 sm:pt-8 md:pt-12 lg:pt-16 xl:pt-20 pb-4 sm:pb-6 md:pb-8 px-4 sm:px-5 md:px-6 lg:px-7 ${position !== "center" ? "backdrop-blur-[7px]" : ""}`}>
                    {/* Blur overlay for non-center cards */}
                    {!isCenter && (
                      <div 
                        className="absolute inset-0 bg-white/40 backdrop-blur-[3px] rounded-[20px] z-10"
                        style={{
                          backdropFilter: 'blur(3px)',
                          WebkitBackdropFilter: 'blur(3px)',
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <QuotesIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-gray-300 mb-3 sm:mb-4 md:mb-5 lg:mb-6" />
                      <p className="text-sm sm:text-base md:text-lg lg:text-xl font-normal text-[#475467] leading-[130%] tracking-[-1%] mb-4 sm:mb-5 md:mb-6 lg:mb-8">
                        {testimonial.quote}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <h4 className="font-medium text-[#050B17] lg:text-[20px] sm:text-base text-sm leading-[130%] tracking-[-0.25px]">
                          {testimonial.name}
                        </h4>
                        <p className="text-[#475467] font-normal lg:text-base sm:text-sm text-xs leading-[140%] tracking-[-0.25px]">
                          {testimonial.role}{testimonial.company ? `, ${testimonial.company}` : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CustomersSaySection };
